import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { sessionAPI } from '../../services/api';
import { FaCalendarAlt, FaPlus, FaEdit, FaTrash, FaArrowLeft, FaCheckCircle, FaClock } from 'react-icons/fa';
import {
    DashboardHeader,
    StatusBadge,
    LoadingSpinner,
    EmptyState
} from '../../components/dashboard';

const ManageSessions = () => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        loadSessions();
    }, []);

    const loadSessions = async () => {
        try {
            setLoading(true);
            const response = await sessionAPI.getAll();
            const newSessions = response.sessions || response || [];
            // CRITICAL: Only update state if data actually changed to prevent infinite loops
            setSessions(prev => {
                const prevStr = JSON.stringify(prev);
                const newStr = JSON.stringify(newSessions);
                return prevStr === newStr ? prev : newSessions;
            });
        } catch (err) {
            console.error('Failed to load sessions:', err);
            toast.error('Failed to load sessions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-6 py-8">
                <Link to="/dashboard?tab=sessions" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors">
                    <FaArrowLeft className="text-sm" />
                    <span className="font-medium">Back to Dashboard</span>
                </Link>

                <div className="flex items-center justify-between mb-6">
                    <DashboardHeader
                        icon={FaCalendarAlt}
                        title="Session Management"
                        subtitle="Manage academic sessions and cohorts"
                    />
                    <button
                        onClick={() => navigate('/admin/sessions/create')}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm hover:shadow-md"
                    >
                        <FaPlus /> Create New Session
                    </button>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : sessions.length === 0 ? (
                    <div className="text-center">
                        <EmptyState
                            icon={FaCalendarAlt}
                            message="No sessions found. Create your first academic session to get started."
                        />
                        <button
                            onClick={() => navigate('/admin/sessions/create')}
                            className="mt-4 flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium mx-auto"
                        >
                            <FaPlus /> Create Session
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {sessions.map((session) => (
                            <div key={session.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{session.name}</h3>
                                        <StatusBadge status={session.status || 'closed'} />
                                    </div>
                                </div>

                                {session.description && (
                                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{session.description}</p>
                                )}

                                <div className="space-y-2 mb-4">
                                    {session.start_date && (
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <FaClock className="text-gray-400" />
                                            <span>Start: {new Date(session.start_date).toLocaleDateString()}</span>
                                        </div>
                                    )}
                                    {session.end_date && (
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <FaClock className="text-gray-400" />
                                            <span>End: {new Date(session.end_date).toLocaleDateString()}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-2">
                                    <Link
                                        to={`/admin/sessions/${session.id}`}
                                        className="flex-1 text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                    >
                                        <FaEdit className="inline mr-1" /> View Details
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageSessions;
